import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../auth/AuthContext";

const Header = ({ onSearchChange, onCategoryChange, activeCategory, searchTerm }) => {
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [balance, setBalance] = useState(null);
  const { user, logout, getFreshToken } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/categorias`);
        setCategories(response.data);
      } catch (error) {
        console.error("Erro ao buscar categorias:", error);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchBalance = async () => {
      if (user) {
        try {
          // Obtém token fresco diretamente do AuthContext
          const token = await getFreshToken();

          if (token) {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/wallet/balance`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            setBalance(response.data.balance);
          }
        } catch (error) {
          console.error("Erro ao buscar saldo:", error);
        }
      }
    };
    fetchBalance();
  }, [user]);

  const handleSearch = (e) => {
    onSearchChange(e.target.value);
  };

  const handleLogoClick = () => {
    // Força o recarregamento voltando para a Home
    window.location.href = '/';
  };

  return (
    <header style={{ 
      backgroundColor: 'var(--glass-bg)', 
      backdropFilter: 'var(--glass-blur)', 
      borderBottom: '1px solid var(--border-color)', 
      position: 'relative', 
      zIndex: 2100 
    }}>
      <div className="container">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '100px', padding: '10px 0' }}>
          {/* Logo */}
          <div
            onClick={handleLogoClick}
            style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', marginRight: 'var(--spacing-xl)', cursor: 'pointer' }}
          >
            <img src="/logo-mercado.png" alt="Mercado Íntimo" style={{ height: '45px', width: 'auto', borderRadius: 'var(--radius-sm)' }} />
          </div>

          {/* Search Bar */}
          <div style={{ flex: 1, maxWidth: '640px', margin: '0 var(--spacing-xl)' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              backgroundColor: 'var(--background-white)', 
              borderRadius: 'var(--radius-lg)', 
              padding: '12px 24px', 
              boxShadow: 'var(--shadow-sm)',
              border: '1.5px solid var(--border-color)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--primary-color)'}
            onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border-color)'}
            >
              <input
                type="text"
                placeholder="Procure por lingeries, sextoys, cosméticos sensuais e muito mais..."
                value={searchTerm || ""}
                onChange={handleSearch}
                style={{ 
                  border: 'none', 
                  outline: 'none', 
                  width: '100%', 
                  fontSize: '15px', 
                  color: 'var(--text-primary)',
                  fontWeight: '500',
                  background: 'transparent'
                }}
              />
              <i className="fas fa-search" style={{ color: 'var(--primary-color)', fontSize: '18px', marginLeft: '10px', cursor: 'pointer' }}></i>
            </div>
          </div>

          {/* User Actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '25px' }}>
            <Link to="/favoritos" style={{ color: 'var(--accent-color)', fontSize: '20px' }}>
              <i className="far fa-heart"></i>
            </Link>
          </div>
        </div>

        {/* Categories - Dropdown Menu */}
        <div style={{ position: 'relative', marginTop: '0' }}>
          <div style={{ 
            display: 'flex', 
            gap: '20px', 
            paddingBottom: '5px', 
            flexWrap: 'nowrap', // Force single line
            alignItems: 'center'
          }}>

            
            {/* Main Categories (First 8) */}
            {categories.slice(0, 8).map((cat) => (
              <div
                key={cat.id}
                onMouseEnter={() => setHoveredCategory(cat.id)}
                onMouseLeave={() => setHoveredCategory(null)}
                style={{ position: 'relative' }}
              >
                <button
                  onClick={() => onCategoryChange(cat.name, null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: activeCategory === cat.name ? 'var(--primary-color)' : 'var(--text-secondary)',
                    fontWeight: activeCategory === cat.name ? '700' : '600',
                    fontFamily: 'var(--font-title)',
                    fontSize: '14px',
                    cursor: 'pointer',
                    padding: '12px 0',
                    borderBottom: activeCategory === cat.name ? '2px solid var(--primary-color)' : '2px solid transparent',
                    whiteSpace: 'nowrap',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  onMouseEnter={(e) => {
                    if (activeCategory !== cat.name) e.currentTarget.style.color = 'var(--primary-color)';
                  }}
                  onMouseLeave={(e) => {
                    if (activeCategory !== cat.name) e.currentTarget.style.color = 'var(--text-secondary)';
                  }}
                >
                  {cat.name} {cat.subcategories && cat.subcategories.length > 0 && <i className="fas fa-chevron-down" style={{ fontSize: '9px', opacity: 0.7 }}></i>}
                </button>

                {/* Subcategories Dropdown */}
                {hoveredCategory === cat.id && cat.subcategories && cat.subcategories.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    backgroundColor: 'var(--background-white)',
                    boxShadow: 'var(--shadow-md)',
                    borderRadius: 'var(--radius-md)',
                    padding: '12px',
                    zIndex: 1000,
                    minWidth: '220px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    border: '1px solid var(--border-color)'
                  }}>
                    {cat.subcategories.map((sub) => (
                      <button
                        key={sub.id}
                        onClick={() => {
                          onCategoryChange(cat.name, sub.name);
                          setHoveredCategory(null);
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          textAlign: 'left',
                          padding: '10px 14px',
                          color: 'var(--text-primary)',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          borderRadius: 'var(--radius-sm)',
                          transition: 'all 0.2s',
                          width: '100%'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = 'var(--primary-light)';
                          e.target.style.color = 'var(--primary-color)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.color = 'var(--text-primary)';
                        }}
                      >
                        {sub.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* "Mais Categorias" Dropdown */}
            {categories.length > 8 && (
              <div
                onMouseEnter={() => setHoveredCategory('more')}
                onMouseLeave={() => setHoveredCategory(null)}
                style={{ position: 'relative' }}
              >
                <button
                  style={{
                    background: 'none',
                    border: 'none',
                    color: hoveredCategory === 'more' ? 'var(--primary-color)' : 'var(--text-secondary)',
                    fontWeight: hoveredCategory === 'more' ? '700' : '500',
                    fontSize: '14px',
                    cursor: 'pointer',
                    padding: '12px 0',
                    borderBottom: hoveredCategory === 'more' ? '2px solid var(--primary-color)' : '2px solid transparent',
                    whiteSpace: 'nowrap',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  Mais Categorias <i className="fas fa-chevron-down" style={{ fontSize: '9px', opacity: 0.7 }}></i>
                </button>

                {hoveredCategory === 'more' && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    backgroundColor: 'var(--background-white)',
                    boxShadow: 'var(--shadow-md)',
                    borderRadius: 'var(--radius-md)',
                    padding: '12px',
                    zIndex: 1000,
                    minWidth: '240px',
                    maxHeight: '450px',
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    border: '1px solid var(--border-color)'
                  }}>
                    {categories.slice(8).map((cat) => (
                      <div key={cat.id} style={{ position: 'relative' }}>
                        <button
                          onClick={() => onCategoryChange(cat.name, null)}
                          style={{
                            background: 'none',
                            border: 'none',
                            textAlign: 'left',
                            padding: '10px 14px',
                            color: 'var(--text-primary)',
                            fontSize: '14px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            borderRadius: 'var(--radius-sm)',
                            transition: 'all 0.2s',
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--primary-light)';
                            e.currentTarget.style.color = 'var(--primary-color)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = 'var(--text-primary)';
                          }}
                        >
                          {cat.name}
                          {cat.subcategories && cat.subcategories.length > 0 && <i className="fas fa-chevron-right" style={{ fontSize: '9px', opacity: 0.5 }}></i>}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </header >
  );
};

export default Header;
